/**
 * Entry point — IIFE bootstrap.
 *
 * Initialization order:
 * 1. Duplicate script guard
 * 2. Script tag parsing (API URL, website ID, pre-config)
 * 3. Selector mode check (early exit)
 * 4. Eager side effects (captureClickIds, getFbc)
 * 5. History API wrappers
 * 6. popstate listener
 * 7. Polling fallback
 * 8. Dedup pruning interval
 * 9. DOM event listeners (click, form submit)
 * 10. Public API
 * 11. Auto-init if website ID in script tag
 */

import { state } from "./state.js";
import { DEV_API_URL, URL_PARAM_SELECTOR, STORAGE_KEY_SELECTOR } from "./constants.js";
import { startSelectorMode } from "./selector-mode.js";
import { captureClickIds, getFbc, adoptArrivingAnchor } from "./cookies.js";
import { captureSourceParams } from "./source-params.js";
import { installHistoryWrappers, startPolling, onUrlChange } from "./spa-navigation.js";
import { syncCanonicalUrl } from "./facebook-pixel.js";
import { startDedupPruning } from "./config.js";
import { installDomListeners } from "./dom-listeners.js";
import { installAjaxFormDetection } from "./ajax-form-detection.js";
import { loadPersistedUserData } from "./user-data-store.js";
import { captureEmailLinkParam } from "./pii-capture.js";
import { buildPublicApi } from "./public-api.js";
import { installCommandQueue } from "./command-queue.js";

// G1: Double script prevention
if ((window as Window).__TST_LOADED) {
  if (console && console.warn) {
    console.warn("[TheSocialTap] Script already loaded — ignoring duplicate include (a ?id on this include is ignored; the first loader wins).");
  }
  // A GTM tag may have created TheSocialTapQ after the first load drained it;
  // take the queue over against the existing API so its commands still land
  if ((window as Window).TheSocialTap) {
    installCommandQueue();
  }
} else {
  (window as Window).__TST_LOADED = true;

  // --- Script tag parsing ---
  const scripts = document.getElementsByTagName("script");
  // document.currentScript is HTMLOrSVGScriptElement; alleen de HTML-variant
  // heeft `src`. In de praktijk is het altijd een <script>, maar het type moet
  // dat wel zeggen.
  const currentScript = (document.currentScript ||
    scripts[scripts.length - 1]) as HTMLScriptElement | null;

  if (currentScript) {
    const src = currentScript.src;
    let queryWebsiteId = "";
    if (src) {
      const url = new URL(src);
      state.apiUrl = url.origin;
      // GTM's injectScript cannot set script attributes; the template passes
      // the website id as t.js?id=<uuid>. The data attribute keeps precedence
      // so the t-loader/coreUrl flow is unaffected.
      queryWebsiteId = url.searchParams.get("id") || "";
    }
    state.websiteId = currentScript.getAttribute("data-website-id") || queryWebsiteId;
    // Here, and not at install time: this is the one moment the tag we are
    // reading is certainly our own.
    state.ecommerceDisabled = currentScript.getAttribute("data-tst-ecommerce") === "off";
    state.wooCommercePlugin = currentScript.getAttribute("data-tst-woocommerce") === "1";
    state.metaWooBrowserExternal = currentScript.getAttribute("data-tst-meta-browser") === "pmw";
    state.pixelManagerPresent = currentScript.getAttribute("data-tst-pixel-manager") === "1";

    // C1: Support pre-fetched config from loader (data-config attribute)
    const preConfig = currentScript.getAttribute("data-config");
    if (preConfig) {
      try {
        state.config = JSON.parse(preConfig);
      } catch {
        state.config = null;
      }
    }
  }

  // Fallback for development
  if (!state.apiUrl) {
    state.apiUrl = DEV_API_URL;
  }

  // --- Debug mode (activated by ?tst_debug in URL) ---
  try {
    state.debug = new URLSearchParams(window.location.search).has("tst_debug");
  } catch {
    // URL parsing failed — debug stays off
  }

  // --- Selector mode check (early exit) ---
  // Uses hash fragment (#tst_select=sessionId) so it survives server-side redirects
  // (hash is never sent to the server). Also persists in sessionStorage for page navigations.
  let selectorActivated = false;
  try {
    const hashPrefix = "#" + URL_PARAM_SELECTOR + "=";
    const hash = window.location.hash;
    const selectorParam = hash.startsWith(hashPrefix) ? hash.slice(hashPrefix.length) : null;

    if (selectorParam) {
      // Store in sessionStorage for subsequent page navigations
      sessionStorage.setItem(STORAGE_KEY_SELECTOR, selectorParam);

      // Strip hash from URL
      history.replaceState(null, "", window.location.pathname + window.location.search);

      startSelectorMode(selectorParam);
      selectorActivated = true;
    } else {
      // No hash param — check sessionStorage (page navigation within the site)
      const storedSessionId = sessionStorage.getItem(STORAGE_KEY_SELECTOR);
      if (storedSessionId) {
        startSelectorMode(storedSessionId);
        selectorActivated = true;
      }
    }
  } catch {
    // Hash parsing or sessionStorage access failed
  }

  /**
   * De publieke API bestaat ALTIJD, ook in de elementkiezer.
   *
   * Dit stond binnen de `if (!selectorActivated)` hieronder, en dat is een
   * valkuil met een groot bereik. Een klant roept `TheSocialTap.consent(...)`
   * aan vanuit zijn cookiebanner. Bestaat het object niet, dan is dat een bare
   * identifier en gooit JavaScript een ReferenceError. Die stopt niet alleen
   * onze aanroep maar de hele klik-handler, en daar staat bij een typische
   * banner ook het bewaren van de keuze en het wegklikken van de balk in. Eén
   * regel die faalt neemt dan de hele banner mee.
   *
   * Registreren vuurt niets af. Wat er in de kiezer niet mag gebeuren is meten,
   * en dat blijft geregeld doordat `init()` hieronder wel binnen de guard
   * staat: zonder init settelt de configuratie nooit, dus de wachtrij houdt
   * track- en pageView-commando's vast in plaats van ze te versturen.
   */
  const TheSocialTap = buildPublicApi();
  window.TheSocialTap = TheSocialTap;
  installCommandQueue();

  if (!selectorActivated) {
    // --- Eager side effects ---
    captureClickIds();
    // Before anything can fire, and before an SPA navigation rewrites the URL:
    // the utm parameters only exist on the landing hit.
    captureSourceParams();
    getFbc();
    loadPersistedUserData();
    // E-mail-link tracking (?tst_em= from ESP merge tags): identifies the
    // visitor from the very first pageview of a newsletter click
    captureEmailLinkParam();

    // The anchor a declared domain sent along, before anything else reads a
    // cookie. Removed from the address bar on the way in: it identifies a
    // visitor, and leaving it there puts it in every screenshot and every
    // referrer the next page sends.
    adoptArrivingAnchor();

    // --- SPA navigation ---
    installHistoryWrappers();

    // Browser back/forward — sync canonical before the FB SDK's own popstate handler reads it
    window.addEventListener("popstate", () => {
      syncCanonicalUrl();
      onUrlChange();
    });

    startPolling();
    startDedupPruning();

    // --- DOM event listeners ---
    installDomListeners();
    installAjaxFormDetection();

    // --- Auto-initialize if website ID provided in script tag ---
    if (state.websiteId) {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => {
          TheSocialTap.init(state.websiteId);
        });
      } else {
        TheSocialTap.init(state.websiteId);
      }
    }
  }
}
